import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Combobox, Modal, MultiSelect, Switch, Tabs, Textarea } from '@indipia/ui-core';
import { worklogApi } from '../api/worklogApi';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { FieldReportHistoryTab } from './FieldReportHistoryTab';
import { PHOTO_POLICY, REPORT_CONTENT_MAX, pickValidPhotos, photoDropNotice, photoSubmitError } from '../config/uploadPolicy';
import { readExifDate } from '../utils/exifDate';
import { submitFieldReportWithCustReq } from '../api/custReqShareApi';
import { CustReqApplySection, CustReqDoneHint } from '../components/custReq/CustReqApplySection';
import { useCustReqApply } from '../components/custReq/useCustReqApply';
import type {
  FieldReportPhotoMeta,
  WorkOrderNotice,
} from '../types/worklog.types';

/**
 * 작업상태 2단(진행중/작업완료) — 이슈발생은 별도 독립 토글(issueOn)이라 여기 포함되지 않는다.
 * WORK_CMPL_YN·ISSUE_TYPE_CODES는 BE에서 이미 독립 필드라, 작업완료와 이슈발생은 동시에 켤 수 있다
 * (예: "작업은 완료했지만 자재 이슈가 있었다").
 */
type WorkStatus = 'ongoing' | 'done';

/**
 * 잔여 5분 미만 타이머용 — 딱딱 끊기는 on/off 깜빡임. `animate-pulse`(부드러운 페이드)보다
 * 눈에 확 띄어야 해서 `steps(2, start)`로 중간 보간 없이 두 상태만 전환한다.
 * ui-core/styles 는 공용 표준이라 건드리지 않고, 이 화면 전용 애니메이션이라 컴포넌트 로컬로 둔다.
 * prefers-reduced-motion 사용자는 깜빡임을 끈다(색만 남음) — 접근성 기본값 유지.
 */
const BLINK_STYLE = `
  /* opacity 는 브라우저가 구간 사이를 미세 보간해 페이드처럼 보일 수 있어 visibility 로 전환한다.
     visibility 는 보간 불가(discrete) 속성이라 타이밍 함수와 무관하게 반드시 즉시 전환된다. */
  @keyframes frw-blink { 50% { visibility: hidden; } }
  .frw-blink { animation: frw-blink 1s steps(2, end) infinite; }
  @media (prefers-reduced-motion: reduce) { .frw-blink { animation: none; } }
`;

/**
 * 반장 현장보고 작성 (외부·공유쿠키 GUEST 화면) — 기능명세 Z-1, API명세 §3.9.
 *
 * §2.14 안내 화면의 CTA '현장보고 작성하기'로 진입한다. 링크를 추가 발급하지 않고
 * 진입 시 이미 발급된 공유 쿠키(share_jwt, 60분)를 그대로 쓴다 — SHARE_SCOPE=RESPOND가
 * 조회와 제출을 겸한다. 현장·작업요청은 서버가 스코프에서 도출하므로 화면은 보내지 않는다.
 *
 * RequireAuth·AppFrame 바깥(F-04 §7)이라 인증 store·앱 셸에 의존하지 않는다.
 */

// 사진·내용 정책은 에이전트 공통 SSOT(config/uploadPolicy)에서 가져온다 — 화면 로컬 상수 금지.
const MAX_PHOTOS = PHOTO_POLICY.maxCount;
const MAX_CONTENT = REPORT_CONTENT_MAX;
/**
 * 공유 세션 TTL(초) — GUEST JWT 60분 (C-05 §3.4.5). 업로드 정책과 무관해 화면에 둔다.
 *
 * [테스트 전용] 카운트다운·경고색 전환을 60분 기다리지 않고 보기 위해 `?ttl=<초>` 로 줄일 수 있다.
 *   예) http://localhost:5778/...?ttl=10          (로컬)
 *   예) https://dev.indipia.ai/...?shareId=541&ttl=10  (개발서버)
 *
 * ⚠️ `import.meta.env.DEV`가 아니라 `MODE !== 'production'`으로 가른다 — `DEV`/`PROD`는
 * "로컬 vite dev 서버냐"만 구분해 **빌드된 산출물은 dev/prod 모드 상관없이 항상 PROD다**.
 * dev.indipia.ai는 `vite build --mode development`(build:dev)로 만든 정적 빌드라 DEV 가드로는
 * 여기서 걸러지지 않는다(운영 `build:prod`만 MODE='production'이라 여기서 확실히 막힌다).
 * ⚠️ 표시용 타이머만 짧아진다 — 실제 GUEST JWT 만료(서버 60분)는 그대로다.
 */
const SESSION_SEC = (() => {
  if (import.meta.env.MODE !== 'production') {
    const ttl = Number(new URLSearchParams(window.location.search).get('ttl'));
    if (Number.isFinite(ttl) && ttl > 0) return ttl;
  }
  return 60 * 60;
})();

/** 초 → 'MM:SS' */
function mmss(sec: number): string {
  const s = Math.max(sec, 0);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** 첨부 대기 사진 — 로컬 미리보기 URL을 함께 들고 있다가 언마운트 시 해제한다. */
interface PendingPhoto {
  file: File;
  previewUrl: string;
}

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

/** 'YYYY-MM-DD' → Date(로컬 자정). new Date(str)로 직접 만들면 UTC로 해석돼 자정 근처에 하루 밀릴 수 있다. */
function parseDateStr(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Date → 로컬 기준 'YYYY-MM-DD'. */
function formatDateStr(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** 로컬 기준 오늘 날짜 'YYYY-MM-DD' — toISOString()은 UTC라 자정 근처에 날짜가 하루 밀릴 수 있어 쓰지 않는다. */
function todayStr(): string {
  return formatDateStr(new Date());
}

/** 작업일자 소급 작성 한도 — 최대 14일 전까지(오늘 이후는 불가). */
const WORK_DATE_MAX_BACK_DAYS = 14;

function shiftDateStr(s: string, deltaDays: number): string {
  const d = parseDateStr(s);
  d.setDate(d.getDate() + deltaDays);
  return formatDateStr(d);
}

const DOW_LABEL = ['일', '월', '화', '수', '목', '금', '토'];

/** 'M월 D일' + 요일 라벨/톤(토=info, 일=error, 평일=기본)을 함께 반환 */
function describeDate(s: string): { md: string; dow: string; dowColor: string } {
  const d = parseDateStr(s);
  const dow = d.getDay();
  return {
    md: `${d.getMonth() + 1}월 ${d.getDate()}일`,
    dow: `${DOW_LABEL[dow]}요일`,
    dowColor: dow === 0 ? 'var(--color-error)' : dow === 6 ? 'var(--color-info)' : 'var(--shell-hint)',
  };
}

export function FieldReportWritePage() {
  const [state, setState] = useState<'loading' | 'error' | 'ready'>('loading');
  const [notice, setNotice] = useState<WorkOrderNotice | null>(null);
  const [tab, setTab] = useState<'write' | 'history'>('write');
  // 전송이력 조회 건수(조회됨/전체) — 현장 바 우측에 표기. 탭이 로드마다 보고한다.
  const [historyCount, setHistoryCount] = useState<{ loaded: number; total: number } | null>(null);
  /** 전송이력 전체 건수 — 탭 타이틀에 상시 노출(현재 탭과 무관). 가벼운 size=1 조회로 totalCount만 가져온다. */
  const [historyTotal, setHistoryTotal] = useState<number | null>(null);

  // ── 폼 ──
  // 공종은 1개만 선택 가능(필수) — 목업 재설계 반영, 기존 다중선택(FilterChip)에서 변경.
  const [workTypeCode, setWorkTypeCode] = useState('');
  /** 최초 진입 시 스케줄상 배정된 공종 — "요청 지정"/"변경됨" 배지 비교 기준. 배정 없으면(스케줄 미연동 등) null */
  const [assignedWorkTypeCode, setAssignedWorkTypeCode] = useState<string | null>(null);
  const [content, setContent] = useState('');
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [lbIndex, setLbIndex] = useState<number | null>(null);
  // 작업상태 2단(진행중/작업완료) — 화면기획서 규칙: 처음 진입 시 아무 것도 선택되지 않은 상태로 시작.
  const [workStatus, setWorkStatus] = useState<WorkStatus | null>(null);
  // 이슈발생 — 작업상태와 독립된 토글(C-1). 켜면 이슈 항목 선택이 나타난다.
  const [issueOn, setIssueOn] = useState(false);
  const [issueCodes, setIssueCodes] = useState<string[]>([]);
  // 작업일자 — 기본값 오늘(YYYY-MM-DD), 편집 가능. 기본값이 항상 채워져 있어 '필수' 뱃지는 두지 않는다.
  const [workDate, setWorkDate] = useState(() => todayStr());

  // ── 제출 ──
  const [submitting, setSubmitting] = useState(false);
  // 전송 오류 — 사용자 안내문(msg) + 원인 코드(code, BE ApiResponse.code)
  const [submitError, setSubmitError] = useState<{ msg: string; code: string | null } | null>(null);
  const [done, setDone] = useState(false);
  /** 전송이력 재조회 트리거 — 제출 성공 시 증가시켜 탭이 최신 목록을 다시 읽게 한다. */
  const [historyNonce, setHistoryNonce] = useState(0);

  // 탭 타이틀의 전송건수 배지 — 현재 어느 탭에 있든(작성 탭이어도) 항상 최신 건수를 보여준다.
  // 목록 전체를 불러오는 FieldReportHistoryTab과 별개로, totalCount만 필요해 size=1로 가볍게 조회한다.
  useEffect(() => {
    const controller = new AbortController();
    worklogApi
      .getFieldReportHistoryShare(0, 1, controller.signal)
      .then((res) => setHistoryTotal(res.data.data.totalCount))
      .catch(() => {/* 배지는 부가 정보 — 조회 실패해도 화면 진행에 영향 없음 */});
    return () => controller.abort();
  }, [historyNonce]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── 사진 순서 변경(길게 눌러 드래그) ──
  /**
   * 길게 눌러 드래그 인식 시간(ms). 이보다 먼저 손을 떼거나 MOVE_CANCEL_PX 넘게 움직이면 일반 탭/스크롤로 처리.
   *
   * 드는 중인 사진은 그리드 흐름에서 분리해 화면 좌표(position:fixed) "고스트"로 마우스/손가락을
   * 그대로 따라가게 한다 — 원래 그리드 셀에 transform으로 띄우면, 드래그 중 배열 순서가 바뀌어
   * 그 항목이 다른 그리드 칸으로 옮겨갈 때마다 translate의 기준점(원래 셀 위치)이 함께 틀어져서
   * 위치가 꼬이는 문제가 있었다. 고스트는 뷰포트 절대좌표라 배열이 몇 번을 재정렬돼도 흔들리지 않는다.
   */
  const LONG_PRESS_MS = 350;
  const MOVE_CANCEL_PX = 8;
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [ghost, setGhost] = useState<{ src: string; left: number; top: number; width: number; height: number } | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const pressRef = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> } | null>(null);
  /** 드래그 시작 시점 썸네일의 화면 좌표(뷰포트 기준) — 고스트 위치 = 이 값 + 포인터 이동량. */
  const dragOriginRef = useRef<{ left: number; top: number; width: number; height: number } | null>(null);
  /** 방금 드래그였는지 — 드래그 종료 직후 발생하는 click이 라이트박스를 여는 것을 막는다. */
  const didDragRef = useRef(false);
  const photoRefs = useRef<Array<HTMLDivElement | null>>([]);

  /**
   * 멱등키 구성요소 — **화면 로딩 시 1회** 발급한다(§3.9).
   * 전송 클릭 시점에 만들면 더블탭·자동재전송이 서로 다른 키가 되어 중복이 걸러지지 않는다.
   * 새로고침하면 새 UUID가 발급되고 입력값도 초기화된다 → 새 보고로 저장된다(여러 건 누적).
   */
  const [submitUuid, setSubmitUuid] = useState(() => crypto.randomUUID());

  /**
   * 세션 잔여시간(초) — **화면 오픈 기준** 60분 카운트다운.
   *
   * ⚠️ 실제 만료와 어긋날 수 있다: 공유 토큰은 알림톡 링크(/v/{token}) 진입 시 1회만
   * 발급되고 SPA 새로고침으로 재발급되지 않는다. 그래서 새로고침하면 여기만 60:00으로
   * 리셋되고 실제 쿠키 만료시각은 그대로다(= 표시가 실제보다 늦어질 수 있음).
   * 정확히 하려면 서버가 만료시각(sessionExpiresAt)을 내려줘야 한다(API명세 부록B #9).
   */
  // 60:00이 잠깐 스쳤다가 바뀌는 대신 59:59로 시작한다(첫 1초는 이미 흐른 것으로 본다).
  const [remainSec, setRemainSec] = useState(SESSION_SEC - 1);
  useEffect(() => {
    const t = setInterval(() => setRemainSec((s) => (s <= 0 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, []);

  // ── 안내 데이터(업체·반장·현장·만료) 조회 ──
  useEffect(() => {
    const controller = new AbortController();
    worklogApi
      .getWorkOrderNoticeShare(controller.signal)
      .then((res) => {
        const d = res.data.data;
        setNotice(d);
        // 담당 공정 기본선택 — 스케줄 미연동이거나 담당이 여럿이면 첫 번째로 기본선택(1개만 선택 가능).
        const assigned = (d.workTypes ?? []).find((w) => w.assigned)?.workTypeCode ?? null;
        setAssignedWorkTypeCode(assigned);
        setWorkTypeCode(assigned ?? '');
        setState('ready');
      })
      .catch((err) => {
        if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setState('error');
      });
    return () => controller.abort();
  }, []);

  /**
   * 미리보기 objectURL 누수 방지 — 언마운트 시에만 일괄 해제.
   *
   * ⚠️ deps를 [photos]로 두면 안 된다 — photos가 바뀔 때마다(사진 추가는 물론 이슈 지정 토글처럼
   * 배열 참조만 바뀌는 경우도) 이전 렌더의 cleanup이 실행되어 "아직 쓰고 있는" 사진의 URL까지
   * 함께 revoke된다. 이미 그려진 <img>는 캐시로 안 깨져 보이지만, 라이트박스처럼 새 <img>를
   * 만들 때 다시 그 URL을 불러오면 깨진 이미지로 보인다. ref로 최신 photos만 참조하고
   * effect 자체는 마운트 시 1회만 등록해 언마운트 때만 정리한다(개별 삭제는 removePhoto가 처리).
   */
  const photosRef = useRef(photos);
  photosRef.current = photos;
  useEffect(
    () => () => {
      photosRef.current.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    },
    [],
  );

  const appendText = useCallback((text: string) => {
    setContent((prev) => (prev + text).slice(0, MAX_CONTENT));
  }, []);
  const stt = useSpeechToText({ onFinalText: appendText });

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    // 공통 검증(개수·용량) — uploadPolicy 한곳에서 처리.
    const result = pickValidPhotos(Array.from(files), photos.length);

    // EXIF 촬영일 7일 초과분 제외. EXIF 없으면(스크린샷·전달본·일부 iOS 등) 판별 불가 → 통과.
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const kept: File[] = [];
    let staleDropped = 0;
    for (const f of result.accepted) {
      const taken = await readExifDate(f);
      if (taken && taken.getTime() < weekAgo) {
        staleDropped += 1;
        continue;
      }
      kept.push(f);
    }

    if (kept.length) {
      setPhotos((prev) => [
        ...prev,
        ...kept.map((f) => ({ file: f, previewUrl: URL.createObjectURL(f) })),
      ]);
    }
    const msgs: string[] = [];
    const notice = photoDropNotice(result);
    if (notice) msgs.push(notice);
    if (staleDropped > 0) msgs.push(`촬영한 지 7일이 지난 사진 ${staleDropped}장은 제외했습니다.`);
    if (msgs.length) setSubmitError({ msg: msgs.join(' '), code: null });
  };

  const removePhoto = (idx: number) => {
    setPhotos((prev) => {
      URL.revokeObjectURL(prev[idx].previewUrl);
      return prev.filter((_, i) => i !== idx);
    });
  };

  /**
   * 사진 순서 변경 — 썸네일을 길게 누른 뒤 마우스/손가락으로 끌어 이동한다.
   */
  const onThumbPointerDown = (e: React.PointerEvent<HTMLDivElement>, idx: number) => {
    // 삭제 버튼 위에서 시작한 누름은 그 버튼의 클릭으로 남겨두고 드래그를 시작하지 않는다.
    if ((e.target as HTMLElement).closest('button')) return;
    didDragRef.current = false;
    const el = e.currentTarget; // DOM 노드 참조 — setTimeout 안에서도 그대로 유효하다.
    const pointerId = e.pointerId;
    const startX = e.clientX;
    const startY = e.clientY;
    // setPointerCapture는 롱프레스가 확정된 뒤(드래그 진입 시점)에만 건다 — pointerdown마다
    // 미리 걸어두면 일반 탭까지 캡처돼 버려 그 뒤 <img> click(라이트박스 열기)이 안 먹는 문제가 있었다.
    const timer = setTimeout(() => {
      try { el.setPointerCapture(pointerId); } catch { /* 이미 손을 뗀 뒤라면 조용히 무시 */ }
      const r = el.getBoundingClientRect();
      dragOriginRef.current = { left: r.left, top: r.top, width: r.width, height: r.height };
      dragIndexRef.current = idx;
      setDragIndex(idx);
      setGhost({ src: photos[idx].previewUrl, left: r.left, top: r.top, width: r.width, height: r.height });
    }, LONG_PRESS_MS);
    pressRef.current = { x: startX, y: startY, timer };
  };

  const onThumbPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const press = pressRef.current;
    if (dragIndexRef.current === null) {
      // 드래그 확정 전 — 임계값 넘게 움직이면 롱프레스를 취소하고 일반 탭/스크롤로 넘긴다.
      if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > MOVE_CANCEL_PX) {
        clearTimeout(press.timer);
        pressRef.current = null;
      }
      return;
    }
    // 드래그 확정 — 기본 스크롤 동작을 막고, 고스트를 뷰포트 절대좌표(원래 위치 + 누적 이동량)로 이동.
    // 그리드 셀 기준 transform이 아니라 뷰포트 좌표라 배열이 재정렬돼도 기준점이 흔들리지 않는다.
    e.preventDefault();
    didDragRef.current = true;
    const origin = dragOriginRef.current;
    if (origin && press) {
      const left = origin.left + (e.clientX - press.x);
      const top = origin.top + (e.clientY - press.y);
      setGhost((g) => (g ? { ...g, left, top } : g));
    }

    let targetIndex = dragIndexRef.current;
    let bestDist = Infinity;
    photoRefs.current.forEach((el, i) => {
      // 드래그 중인 자기 자신(지금은 흐린 빈 자리)은 거리 비교에서 제외한다.
      if (!el || i === dragIndexRef.current) return;
      const r = el.getBoundingClientRect();
      const d = Math.hypot(e.clientX - (r.left + r.width / 2), e.clientY - (r.top + r.height / 2));
      if (d < bestDist) { bestDist = d; targetIndex = i; }
    });
    if (targetIndex !== dragIndexRef.current) {
      const from = dragIndexRef.current;
      setPhotos((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(targetIndex, 0, moved);
        return next;
      });
      dragIndexRef.current = targetIndex;
      setDragIndex(targetIndex);
    }
  };

  const onThumbPointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pressRef.current) clearTimeout(pressRef.current.timer);
    pressRef.current = null;
    dragIndexRef.current = null;
    dragOriginRef.current = null;
    setDragIndex(null);
    setGhost(null);
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const workTypes = useMemo(() => notice?.workTypes ?? [], [notice]);
  const issueTypes = useMemo(() => notice?.issueTypes ?? [], [notice]);
  // 이슈발생을 켰으면 이슈유형 1개 이상 + 현장 사진 1장 이상이 함께 있어야 전송 가능(사진별 이슈 지정은 없다).
  const issueReady = !issueOn || (issueCodes.length > 0 && photos.length > 0);
  const canSubmit = workTypeCode !== '' && workStatus !== null && content.trim().length > 0 && issueReady && !submitting;
  // 고객 요청 반영(S16) — 요청별 [반영했어요] 체크 상태는 훅이 맡는다. 체크하지 않아도 제출은 막지 않는다.
  const custReq = useCustReqApply(notice);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    const meta: FieldReportPhotoMeta[] = photos.map((p, i) => ({
      photoSeq: i,
      source: 'FILE',
      fileName: p.file.name,
      fileExt: extOf(p.file.name),
      fileSize: p.file.size,
    }));
    const crItems = custReq.collect();
    try {
      await submitFieldReportWithCustReq({
        submitUuid,
        workTypeCodes: [workTypeCode],
        issueTypeCodes: issueOn ? issueCodes : undefined,
        workCmplYn: workStatus === 'done' ? 'Y' : 'N',
        workDate,
        reportContent: content.trim(),
        photos: meta.length ? meta : undefined,
        photoFiles: photos.length ? photos.map((p) => p.file) : undefined,
      }, crItems);
      // 중복(같은 submitUuid)이어도 서버는 200 + 최초 결과를 준다 → 동일하게 완료 처리한다(§3.9).
      setDone(true);
      setHistoryNonce((n) => n + 1);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { code?: string } } })?.response;
      const status = resp?.status;
      // BE ApiResponse.code (예: PHOTO_UPLOAD_FAILED) — 원인 식별용으로 문구에 함께 노출한다.
      const code = resp?.data?.code ?? null;
      // 사진 개수·용량 초과는 공통 매핑(uploadPolicy)에서 처리 — 그 외만 화면별 문구.
      const photoMsg = photoSubmitError(code, status);
      let msg: string;
      if (status === 410) msg = '작성 가능 기간이 지났거나 세션이 만료되었습니다. 알림톡 링크로 다시 들어와 주세요.';
      else if (photoMsg) msg = photoMsg;
      else if (status === 404) msg = '공유 링크가 유효하지 않습니다.';
      else if (code === 'PHOTO_UPLOAD_FAILED') msg = '사진 업로드에 실패했습니다. 사진을 빼고 다시 시도하거나 잠시 후 재시도해 주세요.';
      else msg = '전송에 실패했습니다. 잠시 후 다시 시도해 주세요.';
      setSubmitError({ msg, code });
    } finally {
      setSubmitting(false);
    }
  };

  /** 완료 → 전송이력 탭. 작성 탭을 비우고 새 UUID를 발급해 다음 보고를 새 건으로 만든다. */
  const goHistory = () => {
    setDone(false);
    setTab('history');
    // 다음 보고도 최초 진입과 동일하게 담당 공정을 기본선택한다(빈 값으로 두면 재진입 시 기본선택이 사라진다).
    setWorkTypeCode((notice?.workTypes ?? []).find((w) => w.assigned)?.workTypeCode ?? '');
    setContent('');
    photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    setPhotos([]);
    setWorkStatus(null);
    setIssueCodes([]);
    setWorkDate(todayStr());
    setSubmitUuid(crypto.randomUUID());
    custReq.reset();
  };

  if (state === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-text-muted">
        불러오는 중입니다..
      </div>
    );
  }

  if (state === 'error' || !notice) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-8 text-center">
        <h1 className="text-base font-bold text-text-primary">현장보고를 작성할 수 없습니다</h1>
        <p className="text-sm text-text-secondary">공유 링크가 만료되었거나 더 이상 유효하지 않습니다.</p>
      </div>
    );
  }

  const writable = notice.fieldReportWritable;

  return (
    // data-group="02" = 인디공사현황 그룹 액센트(--gt-*) 적용 (D-08 §6.4)
    <div data-group="02" className="min-h-screen" style={{ background: 'var(--shell-bg)' }}>
      {/* 정적 문자열이라 리렌더마다 내용이 바뀌지 않음 → React가 DOM을 재생성하지 않아 애니메이션이 끊기지 않는다 */}
      <style>{BLINK_STYLE}</style>
      <div className="mx-auto flex min-h-screen w-full max-w-[480px] flex-col" style={{ background: 'var(--shell-bg)' }}>

        {/* ─ 헤더: 업체 브랜딩 + 반장 ─ */}
        <header
          className="flex items-center gap-2.5 px-4 py-3"
          style={{ background: 'var(--shell-surface)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
        >
          <div className="min-w-0 flex-1">
            <div className="truncate text-body-md font-bold" style={{ color: 'var(--shell-on-surface)' }}>
              {notice.senderCompanyName}
            </div>
            {notice.senderRepName && (
              <div className="text-caption" style={{ color: 'var(--shell-hint)' }}>
                대표 {notice.senderRepName}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <span
              className="flex items-center justify-center text-caption font-bold"
              style={{
                width: 26, height: 26, borderRadius: 'var(--radius-full)',
                background: 'var(--gt-subtle)', color: 'var(--gt-primary)',
              }}
              aria-hidden
            >
              {notice.recipientName?.slice(0, 1)}
            </span>
            <span className="text-body-sm font-semibold" style={{ color: 'var(--shell-on-surface)' }}>
              {notice.recipientName}
            </span>
          </div>
        </header>

        {/* ─ 탭 ─ */}
        <div className="relative" style={{ background: 'var(--shell-surface)', padding: '0 4px' }}>
          <Tabs
            variant="line"
            value={tab}
            onChange={(v) => setTab(v as 'write' | 'history')}
            tabs={[
              { label: '현장보고 등록', value: 'write' },
              { label: '전송이력조회', value: 'history' },
            ]}
            className="w-full [&>a]:flex-1 [&>a]:text-center"
          />
          {/* 전송이력조회 탭 건수 — Tabs.label은 string 전용이라 탭 위에 원형 배지를 오버레이한다 */}
          {historyTotal != null && (
            <span
              className="pointer-events-none absolute top-1/2 flex h-05 min-w-05 -translate-y-1/2 items-center justify-center rounded-full bg-group px-1 text-[10px] font-bold leading-none text-white"
              style={{ left: 'calc(75% + 52px)' }}
              aria-hidden
            >
              {historyTotal}
            </span>
          )}
        </div>

        {/* ─ 현장 바 (읽기 전용 — 반장은 현장 선택 불가) ─ */}
        <div
          className="flex items-center gap-1.5 px-4 py-2"
          style={{ background: 'var(--shell-bg)', borderBottom: 'var(--border-width-hairline) solid var(--shell-border)' }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--shell-hint)" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
          </svg>
          <span className="truncate text-body-sm font-semibold" style={{ color: 'var(--shell-on-surface-2)' }}>
            {notice.siteName}
          </span>
          {/* 전송이력 탭에서만 — 조회됨/전체 건수를 같은 라인 우측에 표시 */}
          {tab === 'history' && historyCount && (
            <span className="ml-auto shrink-0 text-body-sm tabular-nums" style={{ color: 'var(--shell-on-surface-2)' }}>
              <b style={{ color: 'var(--shell-on-surface)' }}>{historyCount.loaded}</b>
              <span style={{ color: 'var(--shell-hint)' }}> / {historyCount.total}건</span>
            </span>
          )}
        </div>

        {tab === 'history' ? (
          <FieldReportHistoryTab reloadNonce={historyNonce} onCountChange={setHistoryCount} />
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {/* 세션 만료 안내 + 잔여시간 — 임시저장이 없으므로 상시 노출한다(기능명세 Z-1-1) */}
              <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1">
                <p className="text-caption leading-relaxed" style={{ color: 'var(--shell-hint)' }}>
                  60분이 지나면 유효시간 만료되어 전송이 불가합니다.
                </p>
                {/* 5분 미만이면 경고색 전환 + 깜빡임으로 마무리를 유도한다 */}
                <span
                  className={[
                    'shrink-0 text-caption font-bold tabular-nums',
                    remainSec <= 300 ? 'frw-blink' : '',
                  ].join(' ')}
                  style={{ color: remainSec <= 300 ? 'var(--color-error)' : 'var(--gt-primary)' }}
                  role="timer"
                  aria-live="off"
                >
                  남은시간 {mmss(remainSec)}
                </span>
              </div>

              {/* 표준 Alert(D-09 §15.10) — 수제 배너의 배경/글자색 인라인 스타일을 alert-error 로 대체 */}
              {!writable && (
                <Alert kind="error" className="mt-3" role="status">
                  작성 가능 기간이 종료되었습니다. (~{notice.fieldReportExpireAt.replace(/-/g, '.')})
                </Alert>
              )}

              {/* 작업일자 — 보고 전체를 한정하는 항목이라 최상단. 기본값 오늘, 최대 14일 전까지 소급 가능 */}
              <section className="mt-4">
                <div className="flex min-h-06 items-center">
                  <FormLabel>작업일자</FormLabel>
                  {workDate !== todayStr() && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setWorkDate(todayStr())}
                      className="ml-auto !h-auto !px-2 !py-1 gap-1 text-caption"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M3 12a9 9 0 1 1 3 6.7" /><polyline points="3 17 3 21 7 21" />
                      </svg>
                      오늘 날짜로
                    </Button>
                  )}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    aria-label="하루 전으로"
                    disabled={!writable || workDate <= shiftDateStr(todayStr(), -WORK_DATE_MAX_BACK_DAYS)}
                    onClick={() => setWorkDate((d) => shiftDateStr(d, -1))}
                    className="flex shrink-0 items-center gap-1 text-caption font-bold disabled:opacity-40"
                    style={{ height: 36, padding: '0 11px', borderRadius: 'var(--radius-full)', border: 'none', background: 'var(--shell-bg)', color: 'var(--shell-on-surface-2)', cursor: writable ? 'pointer' : 'default' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                    {workDate > shiftDateStr(todayStr(), -WORK_DATE_MAX_BACK_DAYS) && (
                      <span>{describeDate(shiftDateStr(workDate, -1)).md.replace('월 ', '.').replace('일', '')}</span>
                    )}
                  </button>
                  <div className="flex-1 text-center">
                    <span className="text-heading-sm font-extrabold" style={{ color: 'var(--shell-on-surface)' }}>
                      {describeDate(workDate).md}
                    </span>{' '}
                    <span className="text-body-sm font-semibold" style={{ color: describeDate(workDate).dowColor }}>
                      {describeDate(workDate).dow}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="하루 뒤로"
                    disabled={!writable || workDate >= todayStr()}
                    onClick={() => setWorkDate((d) => shiftDateStr(d, 1))}
                    className="flex shrink-0 items-center gap-1 text-caption font-bold disabled:opacity-40"
                    style={{ height: 36, padding: '0 11px', borderRadius: 'var(--radius-full)', border: 'none', background: 'var(--shell-bg)', color: 'var(--shell-on-surface-2)', cursor: writable ? 'pointer' : 'default' }}
                  >
                    {workDate < todayStr() && (
                      <span>{describeDate(shiftDateStr(workDate, 1)).md.replace('월 ', '.').replace('일', '')}</span>
                    )}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                </div>
              </section>

              {/* 공종 선택 — 1개만 선택 가능(필수). 스케줄 배정값과 비교해 "요청 지정"/"변경됨" 배지를 보여준다 */}
              <section className="mt-5">
                <div className="flex items-center gap-1.5">
                  <FormLabel required>공종 선택</FormLabel>
                  {assignedWorkTypeCode && (
                    <span
                      className="inline-flex items-center text-caption font-bold"
                      style={{
                        padding: '2px 7px', borderRadius: 'var(--radius-full)',
                        background: workTypeCode === assignedWorkTypeCode ? 'var(--gt-subtle)' : 'var(--color-warningSubtle)',
                        border: `var(--border-width-hairline) solid ${workTypeCode === assignedWorkTypeCode ? 'var(--gt-primary)' : 'var(--color-warning)'}`,
                        color: workTypeCode === assignedWorkTypeCode ? 'var(--gt-primary)' : 'var(--color-warningStrong)',
                      }}
                    >
                      {workTypeCode === assignedWorkTypeCode ? '요청 지정' : '변경됨'}
                    </span>
                  )}
                </div>
                <div className="mt-2">
                  <Combobox
                    className="field-fill"
                    value={workTypeCode}
                    onChange={(v) => setWorkTypeCode(v)}
                    options={workTypes.map((w) => ({ value: w.workTypeCode, label: w.workTypeName }))}
                    placeholder="공종을 선택해주세요"
                    emptyText="선택 가능한 공종이 없습니다"
                    disabled={!writable}
                  />
                </div>
              </section>

              {/* 작업상태(진행중/작업완료, 2택1) + 이슈발생(독립 토글) — C-1 화면기획서 */}
              <section className="mt-5">
                <FormLabel required>작업상태</FormLabel>
                <div className="mt-2 grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="작업상태">
                  {([
                    { value: 'ongoing', label: '진행중' },
                    { value: 'done', label: '작업완료' },
                  ] as const).map((opt) => {
                    const active = workStatus === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={!writable}
                        onClick={() => setWorkStatus(opt.value)}
                        className="flex flex-col items-center justify-center gap-1.5"
                        style={{
                          minHeight: 58, padding: '10px 4px', borderRadius: 'var(--radius-md)',
                          border: `1.5px solid ${active ? 'var(--gt-primary)' : 'var(--shell-border)'}`,
                          background: active ? 'var(--gt-subtle)' : 'var(--shell-surface)',
                          cursor: writable ? 'pointer' : 'default',
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: 15, height: 15, borderRadius: 'var(--radius-full)',
                            border: active ? '4.5px solid var(--gt-primary)' : '1.5px solid var(--shell-border)',
                            background: 'var(--shell-surface)',
                          }}
                        />
                        <span className="text-body-sm" style={{ fontWeight: active ? 800 : 600, color: active ? 'var(--gt-primary)' : 'var(--shell-on-surface-2)', whiteSpace: 'nowrap' }}>
                          {opt.label}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* 작업완료인데 체크하지 않은 고객 요청이 있으면 한 줄 안내(제출은 막지 않음) */}
                <CustReqDoneHint ctl={custReq} isDone={workStatus === 'done'} />

                {/* 이슈발생 — 작업상태와 독립된 토글. 켜면 아래 이슈 항목이 나타난다(작업완료와 동시에 켤 수 있음).
                    Switch 자체가 내부에 <label>을 렌더하므로(중첩 label 방지), 바깥은 일반 div로 감싼다. */}
                <div
                  className="mt-2 flex w-full items-center justify-between"
                  style={{
                    padding: '12px', borderRadius: 'var(--radius-md)',
                    border: `1px solid ${issueOn ? 'var(--color-warning)' : 'var(--shell-border)'}`,
                    background: issueOn ? 'var(--color-warningSubtle)' : 'var(--shell-surface)',
                  }}
                >
                  <span className="text-body-sm" style={{ fontWeight: 600, color: issueOn ? 'var(--color-warningStrong)' : 'var(--shell-on-surface-2)' }}>
                    이슈 발생
                  </span>
                  <Switch
                    checked={issueOn}
                    disabled={!writable}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setIssueOn(on);
                      if (!on) setIssueCodes([]);
                    }}
                  />
                </div>

                {issueOn && (
                  <Alert kind="warning" className="mt-2.5" role="group" aria-label="이슈 항목">
                    <div className="w-full min-w-0">
                      <div className="flex items-center gap-1.5">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                          <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                        <FormLabel required>이슈 항목</FormLabel>
                        <span className="text-caption font-medium" style={{ color: 'var(--shell-hint)' }}>복수 선택 가능</span>
                      </div>
                      <div className="mt-1.5">
                        {/* 다중선택 콤보박스 — 드롭다운을 닫지 않고 여러 이슈유형을 연속 선택/해제한다 */}
                        <MultiSelect
                          values={issueCodes}
                          onChange={setIssueCodes}
                          options={issueTypes.map((t) => ({ value: t.issueTypeCode, label: t.issueTypeName }))}
                          placeholder="이슈 항목을 1개 이상 선택해 주세요"
                          emptyText="선택 가능한 이슈유형이 없습니다"
                          disabled={!writable}
                        />
                      </div>
                      <p className="mt-1.5 text-caption leading-relaxed" style={{ color: 'var(--shell-hint)' }}>
                        이슈 상황은 아래 <b>보고 내용</b>에 함께 적고, <br/>현장 사진을 1장 이상 첨부해 주세요.
                      </p>
                    </div>
                  </Alert>
                )}
              </section>

              {/* 보고 내용 + STT */}
              <section className="mt-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <FormLabel required>보고 내용</FormLabel>
                    {issueOn && (
                      <span
                        className="inline-flex items-center text-caption font-bold"
                        style={{ padding: '2px 7px', borderRadius: 'var(--radius-full)', background: 'var(--color-warningSubtle)', border: 'var(--border-width-hairline) solid var(--color-warning)', color: 'var(--color-warningStrong)' }}
                      >
                        이슈 내용 포함
                      </span>
                    )}
                  </div>
                  <span className="text-caption" style={{ color: 'var(--shell-hint)' }}>
                    {content.length}/{MAX_CONTENT}
                  </span>
                </div>
                <div className="mt-2">
                  {/*
                    .field 기본 max-width:320px 를 풀어 전체 너비 사용(!max-w-none).
                    .textarea 기본 배경 var(--shell-bg)(회색)은 표준값이지만, 이 화면은 흰 입력창을
                    원해 자식 textarea 배경만 surface(흰색)로 덮는다([&_textarea]:bg-surface-raised).
                  */}
                  <Textarea
                    className="!max-w-none [&_textarea]:bg-surface-raised"
                    value={content}
                    maxLength={MAX_CONTENT}
                    rows={6}
                    disabled={!writable}
                    placeholder={
                      issueOn
                        ? '오늘 작업 내용과 함께 이슈 상황·필요한 조치를 적어 주세요.\n(예: 욕실 배관 위치가 도면과 달라 타일 시공 보류 — 확인 요청)'
                        : '오늘 작업 내용, 진행 상황, 특이사항 등을 입력해 주세요.'
                    }
                    onChange={(e) => setContent(e.target.value)}
                  />
                </div>

                {stt.supported && (
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={stt.toggle}
                      disabled={!writable}
                      aria-label={stt.listening ? '음성입력 중지' : '음성으로 입력'}
                      aria-pressed={stt.listening}
                      className="flex shrink-0 items-center justify-center"
                      style={{
                        width: 48, height: 48, borderRadius: 'var(--radius-full)', border: 'none',
                        background: stt.listening ? 'var(--color-error)' : 'var(--gt-primary)',
                        color: 'var(--gt-on-primary)',
                        cursor: writable ? 'pointer' : 'default', opacity: writable ? 1 : 0.4,
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                        <line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                      </svg>
                    </button>
                    <span className="text-caption" style={{ color: stt.listening ? 'var(--gt-primary)' : 'var(--shell-hint)' }}>
                      {stt.listening
                        ? `듣는 중…${stt.silenceLeft !== null ? ` (${Math.max(stt.silenceLeft, 0)}s)` : ''}`
                        : '음성으로 입력하려면 마이크를 눌러주세요'}
                    </span>
                  </div>
                )}
              </section>

              {/* 현장 사진 */}
              <section className="mt-5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <FormLabel required={issueOn}>현장 사진</FormLabel>
                  {/* 안내문구는 ml-auto로 우측 끝에 둔다 */}
                  <span className="ml-auto text-[10px]" style={{ color: 'var(--shell-hint)' }}>
                    최대 {MAX_PHOTOS}장 · 장당 {PHOTO_POLICY.maxMB}MB
                  </span>
                </div>
                {/* 이슈 모드 안내 — 사진이 없으면 전송 조건을 알린다 */}
                {issueOn && photos.length === 0 && (
                  <p
                    className="mt-2 text-caption leading-relaxed"
                    style={{ padding: '7px 9px', borderRadius: 'var(--radius-sm)', background: 'var(--color-warningSubtle)', border: 'var(--border-width-hairline) dashed var(--color-warning)', color: 'var(--color-warningStrong)' }}
                  >
                    이슈 발생 시 현장 사진을 1장 이상 첨부해야 전송할 수 있습니다.
                  </p>
                )}
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {photos.map((p, i) => (
                    <div
                      key={p.previewUrl}
                      ref={(el) => { photoRefs.current[i] = el; }}
                      className="relative overflow-hidden"
                      // 길게 눌러 드래그로 순서 변경. touchAction:none 이 없으면 터치 브라우저가
                      // 누른 손가락을 스크롤 제스처로 먼저 채가서 롱프레스가 잘 안 잡힌다.
                      // 드는 중인 자리는 흐린 빈 칸으로만 두고, 실제로 손가락을 따라가는 건
                      // 아래 고스트(position:fixed)다 — 그리드 재정렬에 영향받지 않는다.
                      style={{
                        aspectRatio: '1', borderRadius: 'var(--radius-md)', background: 'var(--shell-skeleton)',
                        // 얇고 짙은 그레이 테두리 — 여러 장 첨부 시 각 썸네일 경계를 또렷이 해 정돈감을 준다.
                        border: 'var(--border-width-hairline) solid var(--gray-400)',
                        touchAction: 'none', userSelect: 'none',
                        opacity: dragIndex === i ? 0.25 : 1,
                        cursor: dragIndex === i ? 'grabbing' : undefined,
                      }}
                      onPointerDown={(e) => onThumbPointerDown(e, i)}
                      onPointerMove={onThumbPointerMove}
                      onPointerUp={onThumbPointerEnd}
                      onPointerCancel={onThumbPointerEnd}
                    >
                      <img
                        src={p.previewUrl}
                        alt={`첨부 사진 ${i + 1}`}
                        className="h-full w-full cursor-pointer"
                        style={{ objectFit: 'cover', display: 'block' }}
                        onClick={() => {
                          if (didDragRef.current) return; // 방금 드래그였으면 라이트박스를 열지 않는다
                          setLbIndex(i);
                        }}
                      />
                      <ThumbDeleteButton index={i} onDelete={() => removePhoto(i)}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </ThumbDeleteButton>
                    </div>
                  ))}
                  {writable && photos.length < MAX_PHOTOS && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex flex-col items-center justify-center gap-1"
                      style={{
                        aspectRatio: '1', borderRadius: 'var(--radius-md)',
                        border: 'var(--border-width-hairline) dashed var(--shell-border)', background: 'var(--shell-surface)',
                        color: 'var(--shell-hint)', cursor: 'pointer',
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                      <span className="text-[10px]">사진 추가</span>
                    </button>
                  )}
                </div>
                {/*
                  기획 시안의 '사진선택/사진촬영' 바텀시트 대신 파일 입력 하나로 처리한다.
                  capture 속성 없이 accept=image/* 만 두면 모바일 브라우저가 '카메라/갤러리'
                  선택 시트를 OS 기본으로 띄워준다 — 동일 UX를 커스텀 시트 없이 얻는다.
                */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={PHOTO_POLICY.accept}
                  multiple
                  hidden
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = ''; // 같은 파일 재선택 허용
                  }}
                />
              </section>

              {/* 고객 요청 n건 — 현장 사진 바로 아래. 요청마다 [반영했어요]. 대상이 없으면 숨김 */}
              <CustReqApplySection
                ctl={custReq}
                writable={writable}
                submitting={submitting}
              />

            </div>

            {/* ─ 전송 (에러 배너 + 버튼, sticky) ─ */}
            <div
              className="sticky bottom-0 px-4 py-3"
              style={{ background: 'var(--shell-surface)', borderTop: 'var(--border-width-hairline) solid var(--shell-border)' }}
            >
              {/* 전송 오류 배너 — 버튼 바로 위에 두어 스크롤 위치와 무관하게 항상 보인다. */}
              {/*
                표준 Alert(D-09 §15.10) — .alert 가 flex + gap + 배경/테두리를 제공하므로
                수제 래퍼(인라인 배경·보더·radius)를 걷어냈다. 아이콘은 icon 슬롯(.ai),
                본문은 children. 닫기 버튼은 Alert 규격에 없어 children 안에 유지한다.
              */}
              {submitError && (
                <Alert
                  kind="error"
                  role="alert"
                  className="mb-2"
                  icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-error)" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  }
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[length:var(--type-caption-size)] font-semibold leading-snug" style={{ color: 'var(--color-errorStrong)' }}>
                        {submitError.msg}
                      </p>
                      {submitError.code && (
                        <p className="mt-0.5 text-[10.5px]" style={{ color: 'var(--shell-hint)' }}>
                          오류코드: {submitError.code}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label="오류 안내 닫기"
                      onClick={() => setSubmitError(null)}
                      className="shrink-0"
                      style={{ color: 'var(--color-error)', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </Alert>
              )}
              <Button
                className="w-full"
                disabled={!canSubmit || !writable}
                loading={submitting}
                onClick={submit}
              >
                {submitError ? '다시 전송하기' : '전송하기'}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* 사진 순서변경 드래그 고스트 — 뷰포트 절대좌표라 그리드 아래 어디 두어도 위치가 흔들리지 않는다 */}
      {ghost && (
        <div
          aria-hidden
          className="pointer-events-none fixed overflow-hidden"
          style={{
            left: ghost.left, top: ghost.top, width: ghost.width, height: ghost.height,
            borderRadius: 'var(--radius-md)', border: 'var(--border-width-hairline) solid var(--gray-400)',
            boxShadow: '0 8px 20px var(--alpha-black-400)', transform: 'scale(1.06)', zIndex: 'var(--z-modal)',
          }}
        >
          <img src={ghost.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        </div>
      )}

      <PhotoLightbox
        photos={photos.map((p) => ({ url: p.previewUrl }))}
        index={lbIndex}
        onClose={() => setLbIndex(null)}
        onIndexChange={setLbIndex}
      />

      {/* 전송 완료 */}
      <Modal open={done} onClose={goHistory} size="sm">
        <div className="px-1 py-2 text-center">
          <div
            className="mx-auto flex items-center justify-center"
            style={{ width: 48, height: 48, borderRadius: 'var(--radius-full)', background: 'var(--color-successSubtle)' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 className="mt-3 text-heading-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>전송 완료!</h2>
          <p className="mt-1.5 text-body-sm leading-relaxed" style={{ color: 'var(--shell-on-surface-2)' }}>
            {workStatus === 'done' ? (
              <>
                현장보고가 성공적으로 전송되었습니다.<br />
                <b style={{ color: 'var(--color-success)' }}>작업완료</b>로 함께 전달되었습니다.
              </>
            ) : (
              <>현장보고가 성공적으로 전송되었습니다.<br />사장님에게 알림이 전달됩니다.</>
            )}
          </p>
          <div className="mt-4">
            <Button className="w-full" onClick={goHistory}>전송이력 확인</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/**
 * 썸네일 삭제(×) 버튼. 클릭/탭이 시각적으로 드러나도록 hover·press 상태를 준다:
 * - 기본: 반투명 검정 원
 * - hover/press: 빨간 배경 + 확대(scale) — "눌렀다"는 피드백. 표준에 전용 규정이 없어 자체 구성하되
 *   색은 표준 토큰(--color-error), 전환은 다른 버튼(:hover filter)과 결을 맞춘다.
 */
function ThumbDeleteButton({
  index, onDelete, children,
}: {
  index: number;
  onDelete: () => void;
  children: React.ReactNode;
}) {
  const [pressed, setPressed] = useState(false);
  const [hover, setHover] = useState(false);
  const active = hover || pressed;
  return (
    <button
      type="button"
      aria-label={`사진 ${index + 1} 삭제`}
      onClick={onDelete}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onTouchStart={() => setPressed(true)}
      onTouchEnd={() => setPressed(false)}
      className="absolute right-1 top-1 flex items-center justify-center"
      style={{
        width: 22, height: 22, borderRadius: 'var(--radius-full)', border: 'none',
        background: active ? 'var(--color-error)' : 'var(--alpha-black-600)',
        color: 'var(--gray-0)', cursor: 'pointer',
        transform: pressed ? 'scale(1.25)' : hover ? 'scale(1.15)' : 'scale(1)',
        transition: 'transform .12s, background .12s',
        boxShadow: active ? '0 0 0 2px var(--color-errorSubtle)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function FormLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-body-sm font-bold" style={{ color: 'var(--shell-on-surface)' }}>
      {children}
      {required && (
        <span
          className="px-1 text-[9px] font-bold"
          style={{ background: 'var(--color-errorSubtle)', color: 'var(--color-errorStrong)', borderRadius: 'var(--radius-xs)' }}
        >
          필수
        </span>
      )}
    </span>
  );
}

